import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { RefreshCw, Quote } from "lucide-react";
import { Page, BrandHeader } from "@/components/Brand";
import { Btn } from "@/components/Btn";
import { Thermometer } from "@/components/Thermometer";
import { EMERGENCY_THRESHOLD, sudsLabel } from "@/lib/config";
import { sudsVar } from "@/lib/theme";
import { useCards, useCreateSession, usePrefs } from "@/lib/store";
import { useAuth } from "@/lib/auth";
import { InstallBanner } from "@/components/InstallApp";

function MorningAffirmation() {
  const { data: prefs } = usePrefs();
  const { data: cards } = useCards();
  const card = (cards ?? []).find((c) => c.id === prefs?.affirmationCardId);
  if (!card) return null;
  return (
    <Link to="/affirmations" className="tf-card p-4 flex gap-3 items-start tf-rise tf-d2" data-testid="home-affirmation">
      <Quote size={18} className="text-t-brand shrink-0 mt-1" />
      <span>
        <span className="tf-tag">Your affirmation</span>
        <span className="block text-t-on mt-1">“{card.text}”</span>
      </span>
    </Link>
  );
}

export default function CheckIn() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const create = useCreateSession();
  const [value, setValue] = useState(3);
  const urgent = value >= EMERGENCY_THRESHOLD;

  const onCheckIn = async () => {
    const path = urgent ? "emergency" : "awareness";
    const s = await create.mutateAsync({ path, preSuds: value });
    navigate(`/${path}/${s.id}`);
  };

  return (
    <Page className="flex flex-col min-h-[calc(100vh-4rem)] md:min-h-screen">
      <div className="md:hidden"><BrandHeader /></div>
      <div className="mt-6 md:mt-4 flex flex-col gap-2 tf-rise">
        <p className="text-t-muted text-sm">Hi {user?.name?.split(" ")[0]},</p>
        <h1 className="text-4xl sm:text-5xl font-bold text-t-on leading-tight" data-testid="checkin-title">How are you feeling?</h1>
        <p className="text-t-muted">Slide to your level of distress.</p>
        <Link to="/flip" data-testid="home-flip-script"
          className="mt-2 self-start inline-flex items-center gap-1.5 rounded-full bg-t-brand2 text-t-brand px-4 h-10 font-semibold text-sm hover:brightness-[0.98] transition">
          <RefreshCw size={15} strokeWidth={2.5} /> Flip the Script
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center gap-6 py-6 tf-rise tf-d1">
        <div className="flex flex-col items-end justify-between h-[280px] py-2">
          <div className="text-right"><p className="text-lg font-bold" style={{ color: "var(--hot)" }}>10</p><p className="text-[11px] text-t-muted -mt-1">Crisis</p></div>
          <div className="text-right"><p className="text-[11px] text-t-muted -mb-1">Calm</p><p className="text-lg font-bold" style={{ color: "var(--cold)" }}>0</p></div>
        </div>
        <Thermometer value={value} onChange={setValue} height={300} />
      </div>

      <div className="flex flex-col gap-4 tf-rise tf-d2">
        <div className="flex items-center justify-center gap-2.5">
          <span className="w-3 h-3 rounded-full transition-colors" style={{ background: sudsVar(value) }} />
          <span className="font-semibold text-t-on2" data-testid="suds-label">{sudsLabel(value)}</span>
        </div>
        <Btn size="xl" className="w-full" label={urgent ? "Get help now" : "Check in"} onClick={onCheckIn} loading={create.isPending}
          testId="check-in-button" style={urgent ? { background: "var(--hot)", color: "#fff", boxShadow: "0 10px 30px -12px var(--hot)" } : undefined} />
        <MorningAffirmation />
        <InstallBanner />
      </div>
    </Page>
  );
}
